import { Badge } from '@thefarmersfront/kitchen';

import type { ColDef, ICellRendererParams, IRowNode } from 'ag-grid-community';

import { TextButtonCell } from '~/components/common';
import { ProductSupplierRenderer } from '~/components/product';

import { formatNumber } from '~/utils/common';

import { PRODUCT_STATUS_BADGE_COLOR } from '~/constants';

import type { TypeProduct } from '~/types/product.types';

/**
 * [운영 상품 조회] 페이지 > 테이블 컬럼 정의
 */
const getProductOperatingColumnDefs = ({ onClickViewDetail }: { onClickViewDetail: (skuCode: string) => void }) => {
  const columnDefs: ColDef<TypeProduct>[] = [
    {
      headerName: 'No',
      valueGetter: 'node.rowIndex + 1',
      width: 72,
    },
    {
      field: 'statusName',
      headerName: '상품 등록 상태',
      width: 120,
      minWidth: 100,
      cellRenderer: Badge,
      cellRendererParams: ({ value }: ICellRendererParams<TypeProduct, TypeProduct['statusName']>) => ({
        variant: 'light',
        status: true,
        color: PRODUCT_STATUS_BADGE_COLOR[value as keyof typeof PRODUCT_STATUS_BADGE_COLOR],
        children: value?.replace(/\s/g, ''),
      }),
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'useMethod',
      headerName: '상품 사용여부',
      width: 120,
    },
    {
      field: 'supplierList',
      headerName: '공급사명',
      width: 196,
      minWidth: 100,
      cellRenderer: ProductSupplierRenderer,
      valueFormatter: ({ value }: { value: string[] }) => value.join(', '),
    },
    {
      field: 'skuName',
      headerName: '상품명',
      minWidth: 240,
      flex: 1,
    },
    {
      field: 'skuCode',
      headerName: '상품코드',
      width: 140,
    },
    {
      headerName: '',
      width: 80,
      minWidth: 80,
      cellRenderer: TextButtonCell,
      cellRendererParams: {
        children: '상세보기',
        showDashIfNoValue: false,
        onClick: (node: IRowNode) => onClickViewDetail(node.data.skuCode),
      },
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'temperatureType',
      headerName: '보관 유형',
      width: 100,
    },
    {
      field: 'mainCategory',
      headerName: '대분류',
      width: 140,
    },
    {
      field: 'middleCategory',
      headerName: '중분류',
      width: 140,
    },
    {
      field: 'thirdCategory',
      headerName: '소분류',
      width: 140,
    },
    {
      field: 'fourthCategory',
      headerName: '세분류',
      width: 140,
    },
    {
      field: 'shelfLifeType',
      headerName: '기한정책',
      width: 80,
    },
    {
      field: 'availableDays',
      headerName: '유통/소비기한일수',
      width: 130,
      cellStyle: { textAlign: 'right' },
      valueFormatter: ({ value }: { value?: unknown }) => {
        if (typeof value === 'number') {
          return formatNumber(value);
        } else {
          return '-';
        }
      },
    },
    {
      field: 'sellAvailableDays',
      headerName: '판매가능일수',
      width: 100,
      cellStyle: { textAlign: 'right' },
      valueFormatter: ({ value }: { value?: unknown }) => {
        if (typeof value === 'number') {
          return formatNumber(value);
        } else {
          return '-';
        }
      },
    },
    {
      field: 'sellDeadlineDays',
      headerName: '판매마감일수',
      width: 100,
      cellStyle: { textAlign: 'right' },
      valueFormatter: ({ value }: { value?: unknown }) => {
        if (typeof value === 'number') {
          return formatNumber(value);
        } else {
          return '-';
        }
      },
    },
    {
      field: 'createdDate',
      headerName: '상품 등록일',
      minWidth: 98,
    },
  ];

  return { columnDefs };
};

export default getProductOperatingColumnDefs;
